import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  BellRing,
  BriefcaseBusiness,
  CalendarCheck2,
  HeartHandshake,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const BackToSchoolHero = () => {
  const { user } = useAuth();

  return (
    <section className="relative isolate overflow-hidden bg-[#160c2e] text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(139,92,246,0.34),transparent_34%),radial-gradient(circle_at_88%_12%,rgba(236,72,153,0.30),transparent_30%),radial-gradient(circle_at_70%_90%,rgba(59,130,246,0.22),transparent_35%)]" />
      <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:42px_42px]" />

      <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 md:py-16 lg:px-8 lg:py-20">
        <div className="mb-8 flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold shadow-2xl backdrop-blur-md">
            <BellRing className="h-4 w-4 text-amber-300" />
            Rentrée 2026 : les demandes de garde augmentent
          </div>
        </div>

        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-5 flex justify-center">
            <img
              src="/lovable-uploads/logo_click_gard_entete.png"
              alt="Click&Gard"
              className="h-24 w-24 object-contain drop-shadow-[0_0_35px_rgba(192,132,252,0.55)] md:h-32 md:w-32"
            />
          </div>
          <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            La rentrée arrive.
            <span className="mt-2 block bg-gradient-to-r from-amber-300 via-pink-300 to-violet-300 bg-clip-text text-transparent">
              Votre tranquillité commence ici.
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg md:text-xl">
            Les besoins de garde sont plus importants que jamais. Parents, anticipez sans stress.
            Professionnels, rendez votre profil visible au moment où les familles ont le plus besoin de vous.
          </p>
        </div>

        <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-2">
          <article className="group rounded-[2rem] border border-white/15 bg-white/[0.09] p-6 shadow-2xl backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:bg-white/[0.13] md:p-8">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-400/20 ring-1 ring-violet-300/30">
              <HeartHandshake className="h-6 w-6 text-violet-200" />
            </div>
            <p className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-violet-200">Pour les parents</p>
            <h2 className="text-2xl font-black md:text-3xl">Préparez la rentrée l’esprit tranquille</h2>
            <p className="mt-3 leading-relaxed text-white/70">
              Publiez votre besoin et trouvez une personne de confiance près de chez vous, avant l’afflux de la rentrée.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold text-white/80">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Profils vérifiés</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5"><CalendarCheck2 className="h-3.5 w-3.5" /> Organisation simplifiée</span>
            </div>
            <Link to={user ? "/care-requests" : "/auth?tab=register&role=parent"} className="mt-7 block">
              <Button size="lg" className="h-14 w-full rounded-2xl bg-white font-black text-violet-950 shadow-xl hover:bg-violet-50">
                <Search className="mr-2 h-5 w-5" />
                Je recherche une garde
                <ArrowRight className="ml-auto h-5 w-5" />
              </Button>
            </Link>
          </article>

          <article className="group rounded-[2rem] border border-pink-300/25 bg-gradient-to-br from-pink-500/20 to-violet-500/15 p-6 shadow-2xl backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:from-pink-500/25 md:p-8">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-pink-400/20 ring-1 ring-pink-300/30">
              <BriefcaseBusiness className="h-6 w-6 text-pink-200" />
            </div>
            <p className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-pink-200">Pour les professionnels</p>
            <h2 className="text-2xl font-black md:text-3xl">Soyez visible quand la demande explose</h2>
            <p className="mt-3 leading-relaxed text-white/70">
              Complétez votre profil, indiquez vos disponibilités et recevez les nouvelles demandes de familles près de chez vous.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold text-white/80">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5"><Users className="h-3.5 w-3.5" /> Familles à proximité</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5"><BadgeCheck className="h-3.5 w-3.5" /> Profil valorisé</span>
            </div>
            <Link to="/auth?tab=register&role=pro" className="mt-7 block">
              <Button size="lg" className="h-14 w-full rounded-2xl bg-gradient-to-r from-pink-500 to-violet-500 font-black text-white shadow-xl hover:opacity-90">
                <Sparkles className="mr-2 h-5 w-5" />
                Je propose mes services
                <ArrowRight className="ml-auto h-5 w-5" />
              </Button>
            </Link>
          </article>
        </div>

        <p className="mt-8 text-center text-sm font-semibold text-white/60">
          La rentrée se prépare aujourd’hui sur Click&Gard.
        </p>
      </div>
    </section>
  );
};

export default BackToSchoolHero;
